import { useLocation } from "react-router-dom";

import { useAppSelector } from "@/hooks/useReduxHooks";
import { useGetMyPermissionsQuery } from "@/services/(my-space)/permissionsApi";
import { selectUser } from "@/store/slices/user.slice";
import { PermissionLevelEnum, PLATFORM_SECTION_ENUM } from "@/utils/enums";

export type PermissionAction =
  | "view"
  | "create"
  | "edit"
  | "delete"
  | "settings";

export function usePermissions() {
  const { user } = useAppSelector(selectUser);
  const isCompanySuperAdmin = user?.isCompanySuperAdmin ?? false;
  const roleId = user?.role?.id;

  const { pathname } = useLocation();
  // Company pages are mounted under `/company/...`; My Space pages live at the
  // root (e.g. `/events`). Permission records are scoped per `level`, so the
  // active level must match the area the user is currently in — otherwise a
  // user with individual ("my space") rights would inherit them on company
  // pages, and vice versa.
  const level = pathname.startsWith("/company")
    ? PermissionLevelEnum.COMPANY
    : PermissionLevelEnum.INDIVIDUAL;

  // Self-scoped: the endpoint resolves permissions from the JWT, so no id is
  // passed. Skip only for super admins (who get can() => true below) or when the
  // user has no role at all.
  const { data: roleData, isLoading } = useGetMyPermissionsQuery(undefined, {
    skip: isCompanySuperAdmin || !roleId,
  });

  const permissions = roleData?.data?.permissions ?? [];
  // null = company-wide (no restriction); a company super admin never has a
  // fetched role blob, but always has unrestricted access, so treat that case
  // as company-wide too rather than as "no data yet".
  const accessibleBranchIds = isCompanySuperAdmin
    ? null
    : roleData?.data?.accessibleBranchIds ?? null;

  /**
   * `atLevel` overrides the area derived from the URL — for a page that must
   * ask about the OTHER space (chat, in My Space, opening a company record).
   */
  function can(
    section: PLATFORM_SECTION_ENUM,
    action: PermissionAction,
    atLevel: PermissionLevelEnum = level,
  ): boolean {
    if (isCompanySuperAdmin) return true;
    // A misconfigured caller (e.g. a MODULE_SETTINGS_LINK entry missing its
    // `section`) must not crash the whole sidebar — fail closed instead.
    if (!section) return false;

    // Match the section's permission record for the CURRENT level only.
    // A section can have separate "company" and "individual" records with
    // different rights; do NOT aggregate across levels.
    const sectionMatches = permissions.filter(
      (p) =>
        p.platformSectionName?.toLowerCase() === section.toLowerCase() &&
        p.level?.toLowerCase() === atLevel.toLowerCase(),
    );

    if (sectionMatches.length === 0) return false;

    switch (action) {
      case "view":
        return sectionMatches.some((p) => p.canView);
      case "create":
        return sectionMatches.some((p) => p.canCreate);
      case "edit":
        return sectionMatches.some((p) => p.canEdit);
      case "delete":
        return sectionMatches.some((p) => p.canDelete);
      case "settings":
        return sectionMatches.some((p) => p.canSettings);
    }
  }

  return {
    can,
    isCompanySuperAdmin,
    isLoading: !isCompanySuperAdmin && isLoading,
    // null = company-wide (no restriction); a real array = the branch ids the
    // user's role scope allows — drives the branch dropdown's default
    // selection and single-branch lock.
    accessibleBranchIds,
  };
}
